import React, { useEffect, useState } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

const PRIMARY = '#f25d27';

/**
 * Modal "Adicionar contexto" — substitui as antigas colunas/painéis fixos
 * de "Sugestões NeoFeed" e "Notícias relacionadas". Recebe as fontes de
 * contexto disponíveis para a página (`ArticleWriter`'s prop `contextSources`)
 * e renderiza cada `panel` embutido, com abas quando há mais de uma fonte.
 *
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   sources: Array<{
 *     key: string;
 *     label: string;
 *     icon?: React.ReactNode;
 *     topControl?: React.ReactNode;
 *     panel: React.ReactNode;
 *   }>;
 *   initialTabKey?: string | null;
 * }} props
 */
export default function ContextPickerModal({ open, onClose, sources = [], initialTabKey = null }) {
    const [activeKey, setActiveKey] = useState(initialTabKey || sources[0]?.key || null);

    useEffect(() => {
        if (!open) return;
        const validKeys = sources.map((s) => s.key);
        if (initialTabKey && validKeys.includes(initialTabKey)) {
            setActiveKey(initialTabKey);
        } else if (!validKeys.includes(activeKey)) {
            setActiveKey(sources[0]?.key || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialTabKey]);

    const activeSource = sources.find((s) => s.key === activeKey) || sources[0] || null;
    const showTabs = sources.length > 1;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '12px', height: '80vh', maxHeight: 640, display: 'flex', flexDirection: 'column' } }}
        >
            <DialogTitle sx={{
                fontSize: '1rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pb: showTabs ? 0 : 2,
            }}>
                <TravelExploreIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                <Box sx={{ flex: 1 }}>Adicionar contexto</Box>
                <IconButton size="small" onClick={onClose} aria-label="Fechar" sx={{ color: '#999' }}>
                    <CloseIcon sx={{ fontSize: 19 }} />
                </IconButton>
            </DialogTitle>

            {showTabs && (
                <Box sx={{ px: 2 }}>
                    <Tabs
                        value={activeKey}
                        onChange={(_, value) => setActiveKey(value)}
                        variant="scrollable"
                        scrollButtons={false}
                        sx={{
                            minHeight: 38,
                            '& .MuiTabs-indicator': { backgroundColor: PRIMARY, height: 2 },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                minHeight: 38,
                                px: 1.2,
                                color: '#777',
                            },
                            '& .Mui-selected': { color: `${PRIMARY} !important` },
                        }}
                    >
                        {sources.map((source) => (
                            <Tab key={source.key} value={source.key} label={source.label} />
                        ))}
                    </Tabs>
                </Box>
            )}

            <DialogContent
                dividers
                sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 0 }}
            >
                {!activeSource && (
                    <Typography sx={{ p: 3, textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>
                        Nenhuma fonte de contexto disponível.
                    </Typography>
                )}

                {activeSource && (
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        {activeSource.topControl && (
                            <Box sx={{ p: '10px 16px 0' }}>
                                {activeSource.topControl}
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: '10px 16px 16px' }}>
                            {activeSource.panel}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        backgroundColor: PRIMARY,
                        '&:hover': { backgroundColor: '#d44e1f' },
                        textTransform: 'none',
                        boxShadow: 'none',
                        fontWeight: 600,
                    }}
                >
                    Concluído
                </Button>
            </DialogActions>
        </Dialog>
    );
}
